import React, { useRef, useLayoutEffect } from 'react';
import { motion, useIsPresent } from 'framer-motion';
import { EASE_EXPO_OUT } from '@/lib/motion';

interface SurchargeMethodPanelProps {
  activePanelRef: React.RefObject<HTMLDivElement | null>;
  transitionDuration: number;
  children: React.ReactNode;
}

export const SurchargeMethodPanel: React.FC<SurchargeMethodPanelProps> = ({
  activePanelRef,
  transitionDuration,
  children,
}) => {
  const isPresent = useIsPresent();
  const localRef = useRef<HTMLDivElement>(null);

  // Use a layout effect to forward the local ref to the parent's activePanelRef.
  // This runs depth-first (child before parent), so the parent's useLayoutEffect
  // in useMethodSwapAnimation always sees the latest entering panel.
  // Only the present (entering) panel writes; exiting panels never clear.
  useLayoutEffect(() => {
    if (isPresent && localRef.current) {
      (activePanelRef as React.MutableRefObject<HTMLDivElement | null>).current = localRef.current;
    }
  });

  return (
    <motion.div
      ref={localRef}
      className="col-start-1 row-start-1"
      data-anim-audit="surcharge-method-panel"
      style={{
        position: isPresent ? 'relative' : 'absolute',
        inset: isPresent ? undefined : 0,
        width: '100%',
        pointerEvents: isPresent ? 'auto' : 'none',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: transitionDuration, ease: EASE_EXPO_OUT }}
    >
      {children}
    </motion.div>
  );
};
